import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { getOrganizationSlug, isDesktop, useAuth } from '@/core';
import { useCurrentOrganization } from '@/core/organization';
import { BABY_ELEPHANT } from '@/style/colors';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Person } from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';

import NavbarDropDown from './dropdown';

interface NavbarProps {
  disabled?: boolean;
  loading?: boolean;
  Icon?: React.ReactNode;
  style?: any;
}

const subdomain = getOrganizationSlug();

export default function Navbar({
  disabled,
  loading,
  style,
  ...props
}: NavbarProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { organization } = useCurrentOrganization();
  const router = useRouter();
  const status = useAuth.use.status();

  const { data: customer, refetch: refetchCustomer } = useCustomer();

  useEffect(() => {
    if (status === 'signIn' && !customer) {
      refetchCustomer();
    }
  }, [customer, status, refetchCustomer]);

  return (
    <View style={[styles.container, style]}>
      <View style={styles.itemsContainer}>
        <Pressable
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
          onPress={() => {
            router.navigate('/');
          }}
        >
          {subdomain ? (
            <Image
              style={{
                height: 72,
                aspectRatio: 16 / 9,
                opacity: imageLoaded ? 1 : 0,
              }}
              source={{
                uri:
                  UnistylesRuntime.themeName === 'dark'
                    ? organization?.darkLogo
                    : organization?.lightLogo,
              }}
              contentFit="cover"
              onLoad={() => setImageLoaded(true)}
            />
          ) : (
            <>
              <Image
                style={{ width: 110, height: 50 }}
                source={
                  UnistylesRuntime.themeName === 'dark'
                    ? require('@/../public/images/logos/partner-logo-dark.png')
                    : require('@/../public/images/logos/partner-logo-light.png')
                }
                contentFit="contain"
              />
            </>
          )}
        </Pressable>

        <View style={styles.rightSide}>
          {isDesktop() ? (
            customer?.partnerToken ? (
              <Button
                tx="navbar.switchToPartner"
                onPress={() => {
                  router.replace('/partner/dashboard');
                }}
                type="primarySmall"
                style={{ width: 180 }}
              />
            ) : (
              <Button
                tx="navbar.becomeAPartner"
                onPress={() => {
                  window.open('https://eonrides.com/partners', '_blank');
                }}
                type="secondarySmall"
                style={{ width: 180 }}
              />
            )
          ) : null}
          <Pressable
            disabled={disabled || loading}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
            hitSlop={8}
            onPress={() => {
              setShowDropdown(!showDropdown);
            }}
          >
            {status === 'signIn' && customer ? (
              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                {customer?.firstname || customer?.lastname ? (
                  <View
                    style={{
                      borderRadius: 32,
                      borderWidth: 1,
                      borderColor: theme.colors.card.hover.borderColor,
                      backgroundColor: theme.colors.card.hover.backgroundColor,
                      width: 40,
                      height: 40,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text
                      type="bodyBold"
                      style={{ color: theme.colors.icon.color }}
                    >
                      {`${customer?.firstname?.[0] || ''}${
                        customer?.lastname?.[0] || ''
                      }`}
                    </Text>
                  </View>
                ) : (
                  <Icon
                    type="dressed"
                    icon={<Person />}
                    fillColor={theme.colors.icon.color}
                    width={20}
                    height={20}
                    style={{ borderRadius: 20 }}
                  />
                )}
              </View>
            ) : (
              <Icon
                type="dressed"
                icon={<Person />}
                fillColor={theme.colors.icon.color}
                width={20}
                height={20}
                style={{ borderRadius: 20 }}
              />
            )}

            <Icon
              type="naked"
              icon={<ChevronRight />}
              fillColor={theme.colors.icon.color}
              width={12}
              height={12}
              iconStyle={{
                transform: [{ rotate: '90deg' }],
              }}
              style={{ borderRadius: 20 }}
            />
          </Pressable>

          <NavbarDropDown
            visible={showDropdown}
            onDismiss={() => {
              setShowDropdown(false);
            }}
          />
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    height: 60,
    paddingTop: 16,
    paddingRight: 24,
    paddingLeft: 24,
    paddingBottom: 16,
    backgroundColor: theme.colors.background_1,
    zIndex: 1,
  },
  itemsContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rightSide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    position: 'relative',
  },
  personButton: {
    backgroundColor: BABY_ELEPHANT,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    width: 24,
    height: 32,
    borderRadius: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginButton: {
    width: 200,
  },
}));
